import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { complaintService, identityService, processService } from '../services/api';
import { fetchAllPages } from '../utils/apiPagination';
import {
  complaintDisplayName,
  indexById,
  labelFromMap,
  personDisplayName,
  processoDisplayName,
  shortId,
} from '../utils/lookupLabels';

function unwrap(payload) {
  return Array.isArray(payload) ? payload : payload?.data ?? [];
}

export function useLookupMaps({ persons = false, complaints = false, processos = false } = {}) {
  const { data: personRows = [] } = useQuery(
    ['lookup-persons'],
    () => fetchAllPages((params) => identityService.getPersons(params)),
    { enabled: persons, staleTime: 5 * 60 * 1000 }
  );
  const { data: complaintRows = [] } = useQuery(
    ['lookup-complaints'],
    () => fetchAllPages((params) => complaintService.getComplaints(params)),
    { enabled: complaints, staleTime: 5 * 60 * 1000 }
  );
  const { data: processoRows = [] } = useQuery(
    ['lookup-processos'],
    () => fetchAllPages((params) => processService.getProcessos(params)),
    { enabled: processos, staleTime: 5 * 60 * 1000 }
  );

  const personsList = unwrap(personRows);
  const complaintsList = unwrap(complaintRows);
  const processosList = unwrap(processoRows);

  const personMap = useMemo(() => indexById(personsList, personDisplayName), [personsList]);
  const complaintMap = useMemo(() => indexById(complaintsList, complaintDisplayName), [complaintsList]);
  const processoMap = useMemo(() => indexById(processosList, processoDisplayName), [processosList]);

  return {
    persons: personsList,
    complaints: complaintsList,
    processos: processosList,
    personMap,
    complaintMap,
    processoMap,
    personLabel: (id) => labelFromMap(personMap, id, shortId),
    complaintLabel: (id) => labelFromMap(complaintMap, id, shortId),
    processoLabel: (id) => labelFromMap(processoMap, id, shortId),
    personOptions: personsList.map((p) => ({
      id: p.id,
      label: personDisplayName(p) || shortId(p.id),
    })),
  };
}
